import { ACCEPTED_IMAGE_MEDIA_TYPES, MAX_ACTIVE_IMAGES_PER_PERSON } from '@sentra/contracts';

/**
 * What this screen refuses before a byte is uploaded, and what it deliberately does not.
 *
 * **None of this is a security control, and saying so is the point.** The server decides what an
 * upload IS by sniffing its magic bytes, bounds its pixel count from the header and re-encodes it
 * (AD-52); a browser check is advice a caller can skip. What it buys is the two minutes an operator
 * would otherwise spend uploading a file that was never going to be stored, and a refusal that
 * names the reason while the file picker is still on screen.
 *
 * **The accept-list is the server's own**, from `@sentra/contracts`. A second list here would drift
 * into offering a format the server refuses, or refusing one it accepts — the second being a
 * capability nobody can reach and nothing can see.
 *
 * **The five-image cap is surfaced BEFORE the picker, not after the upload.** The cap is enforced in
 * three layers on the server, so the sixth upload is refused whatever this does; what this changes is
 * whether the operator finds out before or after they chose the photograph.
 */

/** Why a chosen file will not be stored. */
export type ImageRefusal =
  | { readonly kind: 'media_type'; readonly offered: string }
  | { readonly kind: 'cap'; readonly limit: number }
  | { readonly kind: 'empty' };

/** The `accept` attribute for the file picker, from the one accept-list. */
export const FILE_ACCEPT = ACCEPTED_IMAGE_MEDIA_TYPES.join(',');

/**
 * Whether a chosen file is worth sending, given how many photographs the record already has.
 *
 * The media type is the browser's guess from the file's extension and is not trusted by anything;
 * a file the browser cannot type at all is ALLOWED through, because the sniff is what decides and
 * refusing here on a missing guess would refuse a perfectly good photograph from a file manager
 * that reported nothing.
 */
export function refusalFor(
  file: { readonly type: string; readonly size: number },
  activeImages: number,
): ImageRefusal | null {
  if (activeImages >= MAX_ACTIVE_IMAGES_PER_PERSON) {
    return { kind: 'cap', limit: MAX_ACTIVE_IMAGES_PER_PERSON };
  }
  if (file.size === 0) {
    return { kind: 'empty' };
  }
  if (file.type.length > 0 && !ACCEPTED_IMAGE_MEDIA_TYPES.includes(file.type)) {
    return { kind: 'media_type', offered: file.type };
  }
  return null;
}

/** Whether this record has room for another photograph. */
export function hasRoom(activeImages: number): boolean {
  return activeImages < MAX_ACTIVE_IMAGES_PER_PERSON;
}
